import React, { useEffect, useRef, RefObject } from 'react';

const POOL_SIZE = 60;
const BURST_SIZE = 80;

interface CanvasParticle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  life: number;
  maxLife: number;
  radius: number;
  hue: number;
}

function spawnParticle(p: CanvasParticle, speedMult = 1, sizeMult = 1) {
  const angle = Math.random() * Math.PI * 2;
  const speed = (1 + Math.random() * 0.4) * speedMult;
  p.x = 0;
  p.y = 0;
  p.vx = Math.cos(angle) * speed;
  p.vy = Math.sin(angle) * speed;
  p.maxLife = 60 + Math.random() * 80;
  p.life = p.maxLife;
  p.radius = (1.5 + Math.random() * 3) * sizeMult;
  p.hue = Math.random();
}

function spawnBurstParticle(p: CanvasParticle, speedMult = 1, sizeMult = 1) {
  const angle = Math.random() * Math.PI * 2;
  const speed = (5 + Math.random() * 9) * speedMult;
  p.x = 0;
  p.y = 0;
  p.vx = Math.cos(angle) * speed;
  p.vy = Math.sin(angle) * speed;
  p.maxLife = 35 + Math.random() * 40;
  p.life = p.maxLife;
  p.radius = (1 + Math.random() * 3.25) * sizeMult;
  p.hue = Math.random() * 0.35;
}

export interface ParticleCanvasProps {
  /** 'forever' runs indefinitely; a number stops after that many ms. */
  lifetime?: number | 'forever';
  /**
   * In 'fullscreen' mode the canvas covers the full viewport and the origin
   * is read from this ref's bounding rect each frame.
   * In 'relative' mode this prop is ignored — the origin is the canvas center.
   */
  originRef?: RefObject<HTMLElement | null>;
  /** Fire a one-time high-speed burst of particles on mount. */
  withBurst?: boolean;
  /**
   * 'fullscreen' — canvas fills the viewport (use inside a fixed backdrop).
   * 'relative'   — canvas fills its nearest positioned ancestor (use inside
   *               a position:relative container like a slot).
   */
  mode?: 'fullscreen' | 'relative';
  /** Multiplier applied to all particle velocities. Default: 1. */
  speed?: number;
  /** Multiplier applied to all particle radii. Default: 1. */
  particleSize?: number;
}

/**
 * Canvas-based particle emitter that can operate in two modes:
 * - 'fullscreen': full-viewport canvas with an external origin anchor ref.
 * - 'relative': fills its nearest positioned ancestor; origin is canvas center.
 */
export const ParticleCanvas: React.FC<ParticleCanvasProps> = ({
  lifetime = 'forever',
  originRef,
  withBurst = false,
  mode = 'fullscreen',
  speed = 1,
  particleSize = 1,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const resize = () => {
      if (mode === 'fullscreen') {
        canvas.width = window.innerWidth;
        canvas.height = window.innerHeight;
      } else {
        canvas.width = canvas.clientWidth;
        canvas.height = canvas.clientHeight;
      }
    };
    resize();
    window.addEventListener('resize', resize);

    const pool: CanvasParticle[] = Array.from({ length: POOL_SIZE }, () => {
      const p: CanvasParticle = { x: 0, y: 0, vx: 0, vy: 0, life: 0, maxLife: 1, radius: 2, hue: 0 };
      spawnParticle(p, speed, particleSize);
      p.life = Math.floor(Math.random() * p.maxLife);
      return p;
    });

    const burstPool: CanvasParticle[] = withBurst
      ? Array.from({ length: BURST_SIZE }, () => {
          const p: CanvasParticle = { x: 0, y: 0, vx: 0, vy: 0, life: 0, maxLife: 1, radius: 2, hue: 0 };
          spawnBurstParticle(p, speed, particleSize);
          return p;
        })
      : [];

    const startTime = performance.now();
    let rafId: number;

    const tick = () => {
      const elapsed = performance.now() - startTime;
      if (lifetime !== 'forever' && elapsed > lifetime) {
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        return;
      }

      if (mode === 'relative') {
        if (canvas.width !== canvas.clientWidth || canvas.height !== canvas.clientHeight) {
          canvas.width = canvas.clientWidth;
          canvas.height = canvas.clientHeight;
        }
      }

      ctx.clearRect(0, 0, canvas.width, canvas.height);

      let cx: number;
      let cy: number;
      if (originRef?.current) {
        const canvasRect = canvas.getBoundingClientRect();
        const refRect = originRef.current.getBoundingClientRect();
        cx = refRect.left + refRect.width / 2 - canvasRect.left;
        cy = refRect.top + refRect.height / 2 - canvasRect.top;
      } else {
        cx = canvas.width / 2;
        cy = canvas.height / 2;
      }

      for (const p of pool) {
        p.life -= 1;
        if (p.life <= 0) spawnParticle(p, speed, particleSize);

        p.x += p.vx;
        p.y += p.vy;
        p.vx *= 0.995;
        p.vy *= 0.995;

        const t = p.life / p.maxLife;
        const opacity = (t < 0.2 ? t / 0.2 : t) * 0.4;

        const r = Math.round(255 * (1 - p.hue) + 165 * p.hue);
        const g = Math.round(255 * (1 - p.hue) + 180 * p.hue);
        const b = Math.round(255 * (1 - p.hue) + 252 * p.hue);

        ctx.beginPath();
        ctx.arc(cx + p.x, cy + p.y, p.radius, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(${r},${g},${b},${opacity.toFixed(3)})`;
        ctx.fill();
      }

      for (const p of burstPool) {
        if (p.life <= 0) continue;
        p.life -= 1;
        p.x += p.vx;
        p.y += p.vy;
        p.vx *= 0.93;
        p.vy *= 0.93;

        const t = p.life / p.maxLife;
        const opacity = t * 0.85;

        const r2 = Math.round(255 * (1 - p.hue) + 165 * p.hue);
        const g2 = Math.round(255 * (1 - p.hue) + 180 * p.hue);
        const b2 = Math.round(255 * (1 - p.hue) + 252 * p.hue);

        ctx.beginPath();
        ctx.arc(cx + p.x, cy + p.y, p.radius, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(${r2},${g2},${b2},${opacity.toFixed(3)})`;
        ctx.fill();
      }

      rafId = requestAnimationFrame(tick);
    };

    rafId = requestAnimationFrame(tick);

    return () => {
      cancelAnimationFrame(rafId);
      window.removeEventListener('resize', resize);
    };
  }, [lifetime, mode, speed, particleSize]);

  return (
    <canvas
      ref={canvasRef}
      style={
        mode === 'fullscreen'
          ? { position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 1 }
          : { position: 'absolute', inset: 0, width: '100%', height: '100%', pointerEvents: 'none', zIndex: 0 }
      }
    />
  );
};
